import classnames from 'classnames'
import {css} from 'emotion'
import * as RA from 'ramda-adjunct'
import React from 'react'
import type {OmitStrict} from 'type-zoo'
import type {
  TSubmitMajorActionResponse,
  TSubmitMinorActionResponse,
} from '@kapitalist/contract/endpoints'
import {paymentPropriety} from '@kapitalist/contract/gameplay/payment'
import type {TRuleset} from '@kapitalist/contract/ruleset'
import {
  CARDS_DRAWN_PASS_GO,
  debtCollectorCost,
  MAX_CARDS_IN_HAND,
  valueOfCards,
} from '@kapitalist/contract/ruleset'
import type {TStandardPropertyCard} from '@kapitalist/contract/types'
import {show$} from '@kapitalist/contract/utils/millions/copy'
import {unreachableCase} from '@kapitalist/utils/exhaustive'
import {compact} from '@kapitalist/utils/falsiness'
import * as NEL from '@kapitalist/utils/NEList'
import {groupByDiscriminant, partialRecordKeys} from '@kapitalist/utils/Record'
import {safely, safelyOr} from '@kapitalist/utils/safely'
import * as S from '@kapitalist/utils/Set'
import {upperFirst} from '@kapitalist/utils/strings'
import {isInstance} from '@kapitalist/utils/types'
import type {ISingleButtonFormProps} from '~/components/SingleButtonForm'
import {SingleButtonForm} from '~/components/SingleButtonForm'
import type {IDSButtonProps} from '~/design-system/Button'
import DSButton from '~/design-system/Button'
import {DSTextDiv} from '~/design-system/DSText'
import {DSLinkText} from '~/design-system/Link'
import NeighborhoodColorSwatch from '~/game/NeighborhoodColorSwatch'
import type {TSkin} from '~/game/skins'
import {skins} from '~/game/skins'
import {movesLeftForPlay, playHumanOrdinal} from '~/game/types'
import {getObviousPayment, groupedCardDisplayNames} from '~/game/utils'
import Card from '~/models/game/Card'
import {DealBreakerCard, JustSayNoCard} from '~/models/game/Cards/ActionCard'
import CashCard from '~/models/game/Cards/CashCard'
import DualPropertyCard from '~/models/game/Cards/DualPropertyCard'
import PropertyWildCard from '~/models/game/Cards/PropertyWildCard'
import {UniversalRentCard} from '~/models/game/Cards/RentCard'
import ResidenceCard from '~/models/game/Cards/ResidenceCard'
import SpecialPropertyCard from '~/models/game/Cards/SpecialPropertyCard'
import type GameData from '~/models/game/GameData'
import type MajorAction from '~/models/game/MajorAction'
import MajorActionBirthday from '~/models/game/MajorActions/MajorActionBirthday'
import MajorActionChangeNeighborhood from '~/models/game/MajorActions/MajorActionChangeNeighborhood'
import MajorActionDealBreaker from '~/models/game/MajorActions/MajorActionDealBreaker'
import MajorActionDebtCollector from '~/models/game/MajorActions/MajorActionDebtCollector'
import MajorActionDirectionalRent from '~/models/game/MajorActions/MajorActionDirectionalRent'
import MajorActionDiscard from '~/models/game/MajorActions/MajorActionDiscard'
import MajorActionDoubleTheRent from '~/models/game/MajorActions/MajorActionDoubleTheRent'
import MajorActionForcedDeal from '~/models/game/MajorActions/MajorActionForcedDeal'
import MajorActionPassGo from '~/models/game/MajorActions/MajorActionPassGo'
import MajorActionPlayAsCash from '~/models/game/MajorActions/MajorActionPlayAsCash'
import MajorActionPlayToNeighborhood from '~/models/game/MajorActions/MajorActionPlayToNeighborhood'
import MajorActionResidenceToCash from '~/models/game/MajorActions/MajorActionResidenceToCash'
import MajorActionSlyDeal from '~/models/game/MajorActions/MajorActionSlyDeal'
import MajorActionUniversalRent from '~/models/game/MajorActions/MajorActionUniversalRent'
import MinorActionPromptForcedDeal, {
  neighborhoodToAcceptForcedDealDestination,
  possibleDestinationNeighborhoodsForForcedDeal,
} from '~/models/game/MinorActionPrompts/MinorActionPromptForcedDeal'
import MinorActionPromptJustSayNo from '~/models/game/MinorActionPrompts/MinorActionPromptJustSayNo'
import MinorActionAcceptForcedDeal from '~/models/game/MinorActions/MinorActionAcceptForcedDeal'
import MinorActionJustSayNo from '~/models/game/MinorActions/MinorActionJustSayNo'
import MinorActionPaySomething from '~/models/game/MinorActions/MinorActionPaySomething'
import type {
  AnyActionCard,
  AnyCard,
  AnyMajorAction,
  AnyMinorAction,
  AnyRentCard,
} from '~/models/game/types'
import {CardBackImage} from '~/pages/GameLobby/GameCard/stateless'
import {CARD_WIDTH_PX, cardHeightFromWidth} from '~/pages/GameLobbyV2/cardGeometry'
import type {TClickableCardStateV2} from '~/pages/GameLobbyV2/clickableCardState'
import {V2LobbyCardImage} from '~/pages/GameLobbyV2/utils'
import {pluralize} from '~/utils/language'
import styles from './styles.module.css'

interface IProps {
  gameData: GameData

  clickableCardState: TClickableCardStateV2
  setClickableCardState: (
    setFunc: (prevState: TClickableCardStateV2) => TClickableCardStateV2
  ) => void

  chooseMajorAction: (
    majorAction: MajorAction
  ) => Promise<TSubmitMajorActionResponse>
  chooseMajorActionSubmitSuccess: (response: TSubmitMajorActionResponse) => void
  chooseMinorAction: (
    minorAction: AnyMinorAction
  ) => Promise<TSubmitMinorActionResponse>
  chooseMinorActionSubmitSuccess: (response: TSubmitMinorActionResponse) => void

  skin: TSkin
  rules: TRuleset
}

export default class GameBoardCenter extends React.Component<IProps> {
  render() {
    const {gameData, skin} = this.props
    const {currentGamePosition, playerGamePosition} = gameData

    const [mainTurnText, secondaryTurnText] = ((): [string, string] => {
      if (currentGamePosition.inProgressMajorAction) {
        return [
          // TODO this is a bit long for the middle of the table in most cases
          currentGamePosition.inProgressMajorAction.majorAction.userFacingDescription(
            {
              gamePosition: currentGamePosition,
              skin,
              tense: 'past',
            }
          ),
          safelyOr(
            currentGamePosition.inProgressMajorAction.pendingMinorActions[0],
            pma =>
              `Waiting for ${
                playerGamePosition?.currentPlayer?.id === pma.minorActor.id
                  ? 'you'
                  : pma.minorActor.username
              }`,
            ` `
          ),
        ]
      }

      if (currentGamePosition.isMyMajorTurn()) {
        if (currentGamePosition.play === 'discardPlay') {
          return [`You have no moves left`, `End your turn when you're ready`]
        } else {
          return [
            `Make your ${playHumanOrdinal[currentGamePosition.play]} move`,
            `Waiting...`,
          ]
        }
      } else {
        const movesLeft = movesLeftForPlay[currentGamePosition.play]
        return [
          `It’s ${currentGamePosition.majorActorPlayer.username}’s turn`,
          // could remove this since we have the arc markers now
          `${movesLeft} ${pluralize(movesLeft, 'move')} left`,
        ]
      }
    })()

    return (
      <div className={styles.gameBoardCenterWrapper}>
        <div className={styles.textInCenterWrapper}>
          <DSTextDiv size="body-17-demi" color="white">
            {mainTurnText}
          </DSTextDiv>
          <DSTextDiv size="label" color="white">
            {secondaryTurnText}
          </DSTextDiv>
        </div>
        <CenterOfTableCards
          skin={skin}
          cardsInDeck={currentGamePosition.cardsInDeck}
          lastPlayedCard={currentGamePosition.recentlyPlayedCards[0]}
          classNames={{wrapper: styles.gameBoardCenterCards}}
        />
        {this.renderOverlayForClickableCardState()}
        {gameData.winner && (
          <DSTextDiv size="h3" className={classnames(styles.winnerBanner)}>
            <p>{gameData.winner.username} won the game!</p>
          </DSTextDiv>
        )}
      </div>
    )
  }

  // The engine ends a turn on its own once no free move remains, so this is only ever the
  // player's explicit choice: ending their turn early, or the forced discard when over the
  // hand limit.
  DiscardNowButton = ({
    disabled,
    buttonText,
    majorActionDiscard,
  }: {
    majorActionDiscard: MajorActionDiscard
    disabled: boolean
    buttonText: string
  }) => (
    <SingleButtonForm
      globalErrorDisplay="toast"
      buttonStyle="tableAction"
      size="15px"
      disabled={disabled}
      onSubmit={() => this.props.chooseMajorAction(majorActionDiscard)}
      submitSuccess={this.props.chooseMajorActionSubmitSuccess}
    >
      {buttonText}
    </SingleButtonForm>
  )

  renderOverlayForClickableCardState() {
    const {clickableCardState, gameData, rules, skin} = this.props
    const {currentGamePosition, playerGamePosition} = gameData

    if (!playerGamePosition) {
      return null
    }
    const {currentPlayerHand} = playerGamePosition

    switch (clickableCardState.mode) {
      case 'awaitingYourMajorAction':
        const {
          selectedCardInHandId,
          selectedCardInMyPortfolioId,
          consideringMajorAction,
        } = clickableCardState
        const selectedCardInHand = currentPlayerHand.cards.find(
          c => c.id === selectedCardInHandId
        )
        const selectedCardInMyPortfolio = playerGamePosition.currentPlayer.portfolio
          .allCards()
          .find(c => c.id === selectedCardInMyPortfolioId)

        const needToDiscardCount = Math.max(
          0,
          currentPlayerHand.cardCount() - MAX_CARDS_IN_HAND
        )
        const endTurnEarlyButtonText =
          needToDiscardCount <= 0
            ? `Or, end turn early`
            : `Or, discard ${needToDiscardCount} & end turn early...`

        switch (consideringMajorAction?.type) {
          case undefined:
            const selectedCardSomewhere =
              selectedCardInHand ?? selectedCardInMyPortfolio
            if (!selectedCardSomewhere) {
              // discard / end turn is always an option
              return (
                <ListOfActionButtons>
                  {needToDiscardCount <= 0 ? (
                    <this.DiscardNowButton
                      majorActionDiscard={
                        new MajorActionDiscard({
                          type: 'discard',
                          cards: [],
                        })
                      }
                      disabled={false}
                      buttonText={endTurnEarlyButtonText}
                    />
                  ) : (
                    <DSButton
                      buttonStyle="tableAction"
                      size="15px"
                      onClick={() =>
                        this.props.setClickableCardState(prevClickableCardState => ({
                          ...prevClickableCardState,
                          consideringMajorAction: {
                            type: 'discard',
                            selectedCardsToDiscardIds: new Set(),
                          },
                        }))
                      }
                    >
                      {endTurnEarlyButtonText}
                    </DSButton>
                  )}
                </ListOfActionButtons>
              )
            }

            return (
              <AwaitingActionOverlay
                {...this.props}
                clickableCardState={clickableCardState}
                selectedCard={selectedCardSomewhere}
                selectedCardLocation={selectedCardInHand ? 'hand' : 'table'}
              />
            )
          case 'slyDeal':
            return (
              <GenericOverlay>
                <DSTextDiv size="body-17-demi" color="white">
                  What do you want to {skins[skin].vocab.verbs.slyDeal}?
                </DSTextDiv>
                <DSTextDiv size="label" color="white" style={{marginBottom: '10px'}}>
                  Select an opponent’s {skins[skin].vocab.property.singular}
                </DSTextDiv>
                <CenterOfTableCards
                  skin={skin}
                  cardsInDeck={currentGamePosition.cardsInDeck}
                  lastPlayedCard={consideringMajorAction.card}
                  classNames={{
                    deck: styles.dimDeck,
                  }}
                />
                <div className={styles.backButtonAndConfirm}>
                  <DSLinkText
                    underline="onHover"
                    size="label-demi"
                    chevron="left"
                    color="white"
                    onClick={() => {
                      this.props.setClickableCardState(prevClickableCardState => ({
                        ...prevClickableCardState,
                        consideringMajorAction: undefined,
                      }))
                    }}
                  >
                    Back
                  </DSLinkText>
                  <SingleButtonForm
                    globalErrorDisplay="toast"
                    buttonStyle="tableAction"
                    size="15px"
                    disabled={!consideringMajorAction.selectedCardToRequestId}
                    onSubmit={() =>
                      this.props.chooseMajorAction(
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        RA.ensureArray(
                          consideringMajorAction.card.availableMovesFromHand({
                            playerGamePosition:
                              clickableCardState.playerGamePosition,
                            portfolio:
                              clickableCardState.playerGamePosition.currentPlayer
                                .portfolio,
                            rules,
                          })
                        ).find(
                          ma =>
                            ma instanceof MajorActionSlyDeal &&
                            ma.requestedCard.id ===
                              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                              consideringMajorAction.selectedCardToRequestId!
                        )!
                      )
                    }
                    submitSuccess={this.props.chooseMajorActionSubmitSuccess}
                  >
                    Play
                  </SingleButtonForm>
                </div>
              </GenericOverlay>
            )
          case 'forcedDeal':
            return (
              <GenericOverlay>
                <DSTextDiv size="body-17-demi" color="white">
                  What do you want to {skins[skin].vocab.verbs.forcedDeal}?
                </DSTextDiv>
                <DSTextDiv size="label" color="white" style={{marginBottom: '10px'}}>
                  Select the two {skins[skin].vocab.property.plural}
                </DSTextDiv>
                <CenterOfTableCards
                  skin={skin}
                  cardsInDeck={currentGamePosition.cardsInDeck}
                  lastPlayedCard={consideringMajorAction.card}
                  classNames={{
                    deck: styles.dimDeck,
                  }}
                />
                <div className={styles.backButtonAndConfirm}>
                  <DSLinkText
                    underline="onHover"
                    size="label-demi"
                    chevron="left"
                    color="white"
                    onClick={() => {
                      this.props.setClickableCardState(prevClickableCardState => ({
                        ...prevClickableCardState,
                        consideringMajorAction: undefined,
                      }))
                    }}
                  >
                    Back
                  </DSLinkText>
                  <SingleButtonForm
                    globalErrorDisplay="toast"
                    buttonStyle="tableAction"
                    size="15px"
                    disabled={
                      !consideringMajorAction.selectedCardToRequestId ||
                      !consideringMajorAction.selectedCardToOfferId
                    }
                    onSubmit={() =>
                      this.props.chooseMajorAction(
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        RA.ensureArray(
                          consideringMajorAction.card.availableMovesFromHand({
                            playerGamePosition:
                              clickableCardState.playerGamePosition,
                            portfolio:
                              clickableCardState.playerGamePosition.currentPlayer
                                .portfolio,
                            rules,
                          })
                        ).find(
                          ma =>
                            ma instanceof MajorActionForcedDeal &&
                            ma.requestedCard.id ===
                              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                              consideringMajorAction.selectedCardToRequestId! &&
                            ma.offeredCard.id ===
                              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                              consideringMajorAction.selectedCardToOfferId!
                        )!
                      )
                    }
                    submitSuccess={this.props.chooseMajorActionSubmitSuccess}
                  >
                    Play
                  </SingleButtonForm>
                </div>
              </GenericOverlay>
            )
          case 'needsNeighborhood':
            if (consideringMajorAction.card instanceof SpecialPropertyCard) {
              const specialPropertyCard = consideringMajorAction.card

              const cardLocation = currentPlayerHand.cards.find(
                c => c.id === selectedCardInHandId
              )
                ? 'hand'
                : 'table'

              const actions =
                cardLocation === 'hand'
                  ? specialPropertyCard.availableMovesFromHand({
                      portfolio: playerGamePosition.currentPlayer.portfolio,
                      playerGamePosition,
                      rules,
                    })
                  : specialPropertyCard.availableMovesFromTable({
                      portfolio: playerGamePosition.currentPlayer.portfolio,
                      playerGamePosition,
                      rules,
                    })

              const mSelectedNeighborhood = safely(
                consideringMajorAction.selectedPortfolioNeighborhoodId,
                selectedPNId =>
                  actions.find(ptn => ptn.portfolioNeighborhoodId === selectedPNId)
              )

              return (
                <GenericOverlay>
                  <DSTextDiv size="body-17-demi" color="white">
                    Place a {skins[skin].vocab.property.singular}
                  </DSTextDiv>
                  <DSTextDiv
                    size="label"
                    color="white"
                    style={{marginBottom: '10px'}}
                  >
                    Choose where to place it.
                  </DSTextDiv>
                  <CenterOfTableCards
                    skin={skin}
                    cardsInDeck={currentGamePosition.cardsInDeck}
                    lastPlayedCard={currentGamePosition.recentlyPlayedCards[0]}
                    classNames={{
                      deck: styles.dimDeck,
                      lastPlayedCard: styles.dimDeck,
                    }}
                  />
                  <div className={styles.backButtonAndConfirm}>
                    <DSLinkText
                      underline="onHover"
                      size="label-demi"
                      chevron="left"
                      color="white"
                      onClick={() => {
                        this.props.setClickableCardState(prevClickableCardState => ({
                          ...prevClickableCardState,
                          consideringMajorAction: undefined,
                        }))
                      }}
                    >
                      Back
                    </DSLinkText>
                    <SingleButtonForm
                      globalErrorDisplay="toast"
                      buttonStyle="tableAction"
                      size="15px"
                      disabled={!mSelectedNeighborhood}
                      onSubmit={() =>
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        this.props.chooseMajorAction(mSelectedNeighborhood!)
                      }
                      submitSuccess={this.props.chooseMajorActionSubmitSuccess}
                    >
                      {cardLocation === 'hand' ? `Play` : `Move`}
                    </SingleButtonForm>
                  </div>
                </GenericOverlay>
              )
            } else if (consideringMajorAction.card instanceof UniversalRentCard) {
              const chosenPlay = consideringMajorAction.card
                .availableMovesFromHand({
                  portfolio: playerGamePosition.currentPlayer.portfolio,
                  playerGamePosition,
                  rules,
                })
                .find(
                  action =>
                    isInstance(MajorActionUniversalRent)(action) &&
                    action.portfolioNeighborhoodId ===
                      consideringMajorAction.selectedPortfolioNeighborhoodId
                )

              return (
                <GenericOverlay>
                  <DSTextDiv size="body-17-demi" color="white">
                    {consideringMajorAction.card.displayName(skin)}
                  </DSTextDiv>
                  <DSTextDiv
                    size="label"
                    color="white"
                    style={{marginBottom: '10px'}}
                  >
                    Select a {skins[skin].vocab.neighborhood.singular} to{' '}
                    {skins[skin].vocab.verbs.rent} from.
                  </DSTextDiv>
                  <CenterOfTableCards
                    skin={skin}
                    cardsInDeck={currentGamePosition.cardsInDeck}
                    lastPlayedCard={consideringMajorAction.card}
                    classNames={{
                      deck: styles.dimDeck,
                    }}
                  />
                  <div className={styles.backButtonAndConfirm}>
                    <DSLinkText
                      underline="onHover"
                      size="label-demi"
                      chevron="left"
                      color="white"
                      onClick={() => {
                        this.props.setClickableCardState(prevClickableCardState => ({
                          ...prevClickableCardState,
                          consideringMajorAction: undefined,
                        }))
                      }}
                    >
                      Back
                    </DSLinkText>
                    <SingleButtonForm
                      globalErrorDisplay="toast"
                      buttonStyle="tableAction"
                      size="15px"
                      disabled={!chosenPlay}
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      onSubmit={() => this.props.chooseMajorAction(chosenPlay!)}
                      submitSuccess={this.props.chooseMajorActionSubmitSuccess}
                    >
                      Play
                    </SingleButtonForm>
                  </div>
                </GenericOverlay>
              )
            } else if (consideringMajorAction.card instanceof DealBreakerCard) {
              const chosenPlay = consideringMajorAction.card
                .availableMovesFromHand({
                  portfolio: playerGamePosition.currentPlayer.portfolio,
                  playerGamePosition,
                  rules,
                })
                .find(
                  action =>
                    isInstance(MajorActionDealBreaker)(action) &&
                    action.portfolioNeighborhoodId ===
                      consideringMajorAction.selectedPortfolioNeighborhoodId
                )

              return (
                <div className={styles.backButtonAndConfirm}>
                  <DSLinkText
                    underline="onHover"
                    size="label-demi"
                    chevron="left"
                    color="white"
                    onClick={() => {
                      this.props.setClickableCardState(prevClickableCardState => ({
                        ...prevClickableCardState,
                        consideringMajorAction: undefined,
                      }))
                    }}
                  >
                    Back
                  </DSLinkText>
                  <SingleButtonForm
                    globalErrorDisplay="toast"
                    buttonStyle="tableAction"
                    size="15px"
                    disabled={!chosenPlay}
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    onSubmit={() => this.props.chooseMajorAction(chosenPlay!)}
                    submitSuccess={this.props.chooseMajorActionSubmitSuccess}
                  >
                    Play
                  </SingleButtonForm>
                </div>
              )
            } else {
              return unreachableCase(consideringMajorAction.card)
            }
          case 'discard': {
            const majorActionDiscard = new MajorActionDiscard({
              type: 'discard',
              cards: currentPlayerHand.cards.filter(c =>
                consideringMajorAction.selectedCardsToDiscardIds.has(c.id)
              ),
            })

            const tableMoves = RA.ensureArray(
              selectedCardInMyPortfolio?.availableMovesFromTable({
                portfolio: playerGamePosition.currentPlayer.portfolio,
                rules,
                playerGamePosition,
              }) ?? []
            )

            const currentlyDiscardingCount = majorActionDiscard.cards.length
            const buttonText =
              needToDiscardCount <= 0
                ? `End turn`
                : `Discard ${needToDiscardCount} ${pluralize(
                    needToDiscardCount,
                    'card'
                  )}`

            if (selectedCardInMyPortfolio && tableMoves.length) {
              return (
                <AwaitingActionOverlay
                  {...this.props}
                  clickableCardState={clickableCardState}
                  selectedCard={selectedCardInMyPortfolio}
                  selectedCardLocation="table"
                />
              )
            }

            // can't discard more or less than you need to
            const incorrectNumberOfDiscards =
              needToDiscardCount !== currentlyDiscardingCount

            return (
              <div className={styles.backButtonAndConfirm}>
                <DSButton
                  buttonStyle="tableAction"
                  size="15px"
                  disabled={currentGamePosition.play === 'discardPlay'}
                  onClick={() => {
                    this.props.setClickableCardState(prevClickableCardState => ({
                      ...prevClickableCardState,
                      consideringMajorAction: undefined,
                    }))
                  }}
                >
                  Back
                </DSButton>
                <this.DiscardNowButton
                  disabled={incorrectNumberOfDiscards}
                  majorActionDiscard={majorActionDiscard}
                  buttonText={buttonText}
                />
              </div>
            )
          }
          case 'debtCollector': {
            return (
              <GenericOverlay>
                <DSTextDiv size="body-17-demi" color="white">
                  {consideringMajorAction.card.displayName(skin)}
                </DSTextDiv>
                <DSTextDiv size="label" color="white" style={{marginBottom: '10px'}}>
                  Choose who to {skins[skin].vocab.verbs.nonRentDebt}{' '}
                  {show$(debtCollectorCost)}
                </DSTextDiv>
                <CenterOfTableCards
                  skin={skin}
                  cardsInDeck={currentGamePosition.cardsInDeck}
                  lastPlayedCard={consideringMajorAction.card}
                  classNames={{
                    deck: styles.dimDeck,
                  }}
                />
                <div className={styles.backButtonAndConfirm}>
                  <DSLinkText
                    underline="onHover"
                    size="label-demi"
                    chevron="left"
                    color="white"
                    onClick={() => {
                      this.props.setClickableCardState(prevClickableCardState => ({
                        ...prevClickableCardState,
                        consideringMajorAction: undefined,
                      }))
                    }}
                  >
                    Back
                  </DSLinkText>
                  <SingleButtonForm
                    globalErrorDisplay="toast"
                    buttonStyle="tableAction"
                    size="15px"
                    disabled={!consideringMajorAction.selectedPlayerId}
                    onSubmit={() =>
                      this.props.chooseMajorAction(
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        RA.ensureArray(
                          consideringMajorAction.card.availableMovesFromHand({
                            playerGamePosition:
                              clickableCardState.playerGamePosition,
                            portfolio:
                              clickableCardState.playerGamePosition.currentPlayer
                                .portfolio,
                            rules,
                          })
                        ).find(
                          ma =>
                            ma instanceof MajorActionDebtCollector &&
                            ma.minorActorPlayerId ===
                              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                              consideringMajorAction.selectedPlayerId!
                        )!
                      )
                    }
                    submitSuccess={this.props.chooseMajorActionSubmitSuccess}
                  >
                    Play
                  </SingleButtonForm>
                </div>
              </GenericOverlay>
            )
          }
          case 'directionalRent': {
            const mRentAmount =
              playerGamePosition.currentPlayer.portfolio.neighborhoods
                .find(
                  pn =>
                    pn.id === consideringMajorAction.selectedPortfolioNeighborhoodId
                )
                ?.rent()
            const mOpponent = playerGamePosition.opponents.find(
              o => o.id === consideringMajorAction.selectedPlayerId
            )

            return (
              <GenericOverlay>
                <DSTextDiv size="body-17-demi" color="white">
                  {consideringMajorAction.card.displayName(skin)}
                </DSTextDiv>
                <DSTextDiv size="label" color="white" style={{marginBottom: '10px'}}>
                  {((): string => {
                    const rentVerb = skins[skin].vocab.verbs.rent
                    if (mRentAmount && mOpponent) {
                      return `${upperFirst(rentVerb)} ${mOpponent.username} ${show$(
                        mRentAmount
                      )}`
                    } else if (mRentAmount) {
                      return `Choose who to ${rentVerb} ${show$(mRentAmount)}`
                    } else if (mOpponent) {
                      return `Choose how to ${rentVerb} ${mOpponent.username}`
                    } else {
                      return `Select a ${skins[skin].vocab.neighborhood.singular} and an opponent to ${rentVerb}`
                    }
                  })()}
                </DSTextDiv>
                <CenterOfTableCards
                  skin={skin}
                  cardsInDeck={currentGamePosition.cardsInDeck}
                  lastPlayedCard={consideringMajorAction.card}
                  classNames={{
                    deck: styles.dimDeck,
                  }}
                />
                <div className={styles.backButtonAndConfirm}>
                  <DSLinkText
                    underline="onHover"
                    size="label-demi"
                    chevron="left"
                    color="white"
                    onClick={() => {
                      this.props.setClickableCardState(prevClickableCardState => ({
                        ...prevClickableCardState,
                        consideringMajorAction: undefined,
                      }))
                    }}
                  >
                    Back
                  </DSLinkText>
                  <SingleButtonForm
                    globalErrorDisplay="toast"
                    buttonStyle="tableAction"
                    size="15px"
                    disabled={
                      !consideringMajorAction.selectedPlayerId ||
                      !consideringMajorAction.selectedPortfolioNeighborhoodId
                    }
                    onSubmit={() =>
                      this.props.chooseMajorAction(
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        RA.ensureArray(
                          consideringMajorAction.card.availableMovesFromHand({
                            playerGamePosition:
                              clickableCardState.playerGamePosition,
                            portfolio:
                              clickableCardState.playerGamePosition.currentPlayer
                                .portfolio,
                            rules,
                          })
                        ).find(
                          ma =>
                            ma instanceof MajorActionDirectionalRent &&
                            ma.minorActorPlayerId ===
                              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                              consideringMajorAction.selectedPlayerId! &&
                            ma.portfolioNeighborhood.id ===
                              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                              consideringMajorAction.selectedPortfolioNeighborhoodId!
                        )!
                      )
                    }
                    submitSuccess={this.props.chooseMajorActionSubmitSuccess}
                  >
                    Play
                  </SingleButtonForm>
                </div>
              </GenericOverlay>
            )
          }
          default:
            return unreachableCase(consideringMajorAction)
        }
      case 'acceptOrJustSayNo': {
        const validResponses = clickableCardState.minorActionPrompt.validResponses({
          inProgressMajorAction: clickableCardState.inProgressMajorAction,
          gamePosition: currentGamePosition,
          portfolio: playerGamePosition.currentPlayer.portfolio,
          hand: playerGamePosition.currentPlayerHand,
          rules,
        })
        return (
          <ListOfActionButtons>
            {validResponses.map((ma, i) => (
              <SingleButtonForm
                key={i}
                globalErrorDisplay="toast"
                buttonStyle="tableAction"
                size="15px"
                onSubmit={() => this.props.chooseMinorAction(ma)}
                submitSuccess={this.props.chooseMinorActionSubmitSuccess}
              >
                {ma.userFacingDescription({
                  gamePosition: currentGamePosition,
                  inProgressMajorAction: clickableCardState.inProgressMajorAction,
                  skin,
                  majorActor: clickableCardState.inProgressMajorAction.majorActor,
                  minorActor: playerGamePosition.currentPlayer,
                  tense: 'imperativePrompt',
                })}
              </SingleButtonForm>
            ))}
          </ListOfActionButtons>
        )
      }
      case 'chooseNeighborhood': {
        if (
          clickableCardState.for instanceof MinorActionPromptForcedDeal ||
          // a JSN was played on your JSN of a Forced Deal. Behaves just like the
          // initial MinorActionPromptForcedDeal - same validResponses, same UI.
          clickableCardState.for instanceof MinorActionPromptJustSayNo
        ) {
          // TODO this cast is probably ok?
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const inProgressMajorAction = playerGamePosition.inProgressMajorAction!
          const validResponses = clickableCardState.for.validResponses({
            inProgressMajorAction,
            gamePosition: playerGamePosition,
            portfolio: playerGamePosition.currentPlayer.portfolio,
            hand: playerGamePosition.currentPlayerHand,
            rules,
          })

          return (
            <ListOfActionButtons>
              {validResponses.flatMap((ma, i) => {
                // we offer one "accept" button per color and skip the neighborhood picker for non-PWCs,
                // the engine finds the relevant destination from there.
                // Only PWCs use the picker (though I think we could just send color for these too...
                // the only problem would be making that choice not involve a UI with N buttons, so I left
                // the backend accepting neighborhood IDs for PWCs for now to match the UI).
                if (
                  ma instanceof MinorActionAcceptForcedDeal &&
                  !(ma.offeredCard instanceof PropertyWildCard)
                ) {
                  const colors = [...ma.offeredCard.cardTypes()]
                  return colors.map(color => (
                    <SingleButtonForm
                      key={`${i}-${color}`}
                      globalErrorDisplay="toast"
                      buttonStyle="tableAction"
                      size="15px"
                      onSubmit={() => {
                        ma.datasource.destination = {kind: 'asColor', color}
                        return this.props.chooseMinorAction(ma)
                      }}
                      submitSuccess={this.props.chooseMinorActionSubmitSuccess}
                    >
                      Accept as {skins[skin].shortNeighborhoodNames[color]}
                      <NeighborhoodColorSwatch color={color} skin={skin} />
                    </SingleButtonForm>
                  ))
                }

                return [
                  <SingleButtonForm
                    key={i}
                    globalErrorDisplay="toast"
                    buttonStyle="tableAction"
                    size="15px"
                    onSubmit={() => {
                      if (ma instanceof MinorActionAcceptForcedDeal) {
                        const selectedNeighborhood =
                          possibleDestinationNeighborhoodsForForcedDeal({
                            offeredCard: ma.offeredCard,
                            requestedCard: ma.requestedCard,
                            targetedPlayer: playerGamePosition.currentPlayer,
                          }).find(
                            pn =>
                              pn.id ===
                              clickableCardState.selectedPortfolioNeighborhoodId
                          )
                        if (selectedNeighborhood) {
                          ma.datasource.destination =
                            neighborhoodToAcceptForcedDealDestination(
                              selectedNeighborhood
                            )
                        }
                      }
                      return this.props.chooseMinorAction(ma)
                    }}
                    submitSuccess={this.props.chooseMinorActionSubmitSuccess}
                    disabled={
                      clickableCardState.selectedPortfolioNeighborhoodId ===
                        undefined && !isInstance(MinorActionJustSayNo)(ma)
                    }
                  >
                    {ma.userFacingDescription({
                      gamePosition: playerGamePosition,
                      inProgressMajorAction,
                      skin,
                      majorActor: inProgressMajorAction.majorActor,
                      minorActor: playerGamePosition.currentPlayer,
                      tense: 'imperativePrompt',
                    })}
                  </SingleButtonForm>,
                ]
              })}
            </ListOfActionButtons>
          )
        } else {
          return unreachableCase(clickableCardState.for)
        }
      }
      case 'paySomething': {
        const {inProgressMajorAction, selectedCardIds} = clickableCardState
        const validResponses = clickableCardState.minorActionPrompt.validResponses({
          inProgressMajorAction,
          gamePosition: playerGamePosition,
          portfolio: playerGamePosition.currentPlayer.portfolio,
          hand: playerGamePosition.currentPlayerHand,
          rules,
        })

        return (
          <ListOfActionButtons>
            {validResponses.map((minorAction, i) => {
              if (minorAction instanceof MinorActionPaySomething) {
                const properPaymentData = paymentPropriety(
                  minorAction.millionsOwed,
                  playerGamePosition.currentPlayer.portfolio.allValuableCards(),
                  selectedCardIds
                )
                const obviousPayment = getObviousPayment(
                  minorAction,
                  playerGamePosition
                )

                const selectedAmountToPay = ((): string => {
                  switch (properPaymentData.type) {
                    case 'proper':
                      return show$(valueOfCards(properPaymentData.paidWith))
                    case 'invalidCardSelected':
                      return '(error)'
                    case 'moreMoneyOwed':
                    case 'tooManyCards':
                      return show$(properPaymentData.currentPayment)
                    default:
                      return unreachableCase(properPaymentData)
                  }
                })()
                return (
                  <React.Fragment key={i}>
                    {obviousPayment && (
                      <SingleButtonForm
                        globalErrorDisplay="toast"
                        buttonStyle="tableAction"
                        size="15px"
                        onSubmit={() => {
                          minorAction.paidWithCards = obviousPayment.paidWith
                          return this.props.chooseMinorAction(minorAction)
                        }}
                        submitSuccess={this.props.chooseMinorActionSubmitSuccess}
                        disabled={
                          selectedCardIds.size > 0 ||
                          !playerGamePosition.isMyMinorTurn()
                        }
                      >
                        Autopay (
                        {groupedCardDisplayNames(obviousPayment.paidWith, skin).join(
                          ', '
                        )}
                        )
                      </SingleButtonForm>
                    )}

                    <SingleButtonForm
                      globalErrorDisplay="toast"
                      buttonStyle="tableAction"
                      size="15px"
                      disabled={
                        properPaymentData.type !== 'proper' ||
                        !playerGamePosition.isMyMinorTurn()
                      }
                      onSubmit={() => {
                        if (properPaymentData.type !== 'proper') {
                          // Can't happen re: `disabled` above.
                          return Promise.reject(
                            `Tried to pay with an improper payment`
                          )
                        }
                        minorAction.paidWithCards = properPaymentData.paidWith
                        return this.props.chooseMinorAction(minorAction)
                      }}
                      submitSuccess={this.props.chooseMinorActionSubmitSuccess}
                    >
                      {selectedCardIds.size === 0 ||
                      properPaymentData.type === 'invalidCardSelected'
                        ? `Select cards to pay with`
                        : `${
                            obviousPayment ? 'Manually p' : 'P'
                          }ay ${selectedAmountToPay}`}
                    </SingleButtonForm>
                  </React.Fragment>
                )
              }

              // I believe JSN would be the only other choice here, but I'm leaving this somewhat generic just in case
              let otherActionButtonText = minorAction.userFacingDescription({
                gamePosition: playerGamePosition,
                inProgressMajorAction,
                skin,
                majorActor: inProgressMajorAction.majorActor,
                minorActor: playerGamePosition.currentPlayer,
                tense: 'imperativePrompt',
              })
              let disabled = false
              if (minorAction instanceof MinorActionJustSayNo) {
                const selectedCard = playerGamePosition.currentPlayerHand.cards.find(
                  c => c.id === S.first(selectedCardIds)
                )
                if (
                  !(
                    selectedCardIds.size === 1 &&
                    isInstance(JustSayNoCard)(selectedCard)
                  )
                ) {
                  disabled = true
                  otherActionButtonText = `Click your ${Card.displayNameForCard(
                    'justSayNo',
                    skin
                  )} card to use it`
                }
              }

              return (
                <SingleButtonForm
                  key={i}
                  globalErrorDisplay="toast"
                  buttonStyle="tableAction"
                  size="15px"
                  onSubmit={() => this.props.chooseMinorAction(minorAction)}
                  submitSuccess={this.props.chooseMinorActionSubmitSuccess}
                  disabled={!playerGamePosition.isMyMinorTurn() || disabled}
                >
                  {otherActionButtonText}
                </SingleButtonForm>
              )
            })}
          </ListOfActionButtons>
        )
      }
      case 'none':
        return null
      default:
        return unreachableCase(clickableCardState)
    }
  }
}

interface IGenericOverlayProps extends React.HTMLProps<HTMLDivElement> {}
const GenericOverlay = (props: IGenericOverlayProps) => (
  <div {...props} className={classnames(styles.overlay, props.className)} />
)

interface IListOfActionButtonsProps extends React.HTMLProps<HTMLDivElement> {}
const ListOfActionButtons = (props: IListOfActionButtonsProps) => (
  <div {...props} className={classnames(styles.actionButtons, props.className)} />
)

const AwaitingActionOverlay = (
  props: IProps & {
    selectedCard: AnyCard
    selectedCardLocation: 'hand' | 'table'
    clickableCardState: TClickableCardStateV2 & {mode: 'awaitingYourMajorAction'}
  }
) => {
  const {clickableCardState, rules, selectedCard, selectedCardLocation} = props

  const availableMoves =
    selectedCardLocation === 'hand'
      ? RA.ensureArray(
          props.selectedCard.availableMovesFromHand({
            playerGamePosition: clickableCardState.playerGamePosition,
            portfolio: clickableCardState.playerGamePosition.currentPlayer.portfolio,
            rules,
          })
        )
      : RA.ensureArray(
          props.selectedCard.availableMovesFromTable({
            playerGamePosition: clickableCardState.playerGamePosition,
            portfolio: clickableCardState.playerGamePosition.currentPlayer.portfolio,
            rules,
          })
        )

  if (!availableMoves.length) {
    return null
  }

  const shortText = (action: AnyMajorAction): string => {
    if (
      action instanceof MajorActionBirthday ||
      action instanceof MajorActionDoubleTheRent
    ) {
      return `Play card`
    } else if (
      action instanceof MajorActionUniversalRent ||
      action instanceof MajorActionDealBreaker ||
      action instanceof MajorActionDebtCollector ||
      action instanceof MajorActionDirectionalRent ||
      action instanceof MajorActionForcedDeal ||
      action instanceof MajorActionSlyDeal
    ) {
      return `Play card...`
    } else if (action instanceof MajorActionChangeNeighborhood) {
      return `Move ${skins[props.skin].vocab.property.singular}...`
    } else if (action instanceof MajorActionDiscard) {
      // TODO probably can't happen here?
      return `Discard`
    } else if (action instanceof MajorActionPassGo) {
      return `${upperFirst(
        skins[props.skin].vocab.verbs.passGo
      )} (draw ${CARDS_DRAWN_PASS_GO})`
    } else if (action instanceof MajorActionPlayAsCash) {
      return `Play as cash`
    } else if (action instanceof MajorActionResidenceToCash) {
      return `Convert to cash`
    } else if (action instanceof MajorActionPlayToNeighborhood) {
      return `Place on table`
    } else {
      return unreachableCase(action)
    }
  }

  const grouped = groupByDiscriminant('type', availableMoves)

  const playDirectly = (action: AnyMajorAction, key: string) => (
    <PromiseOverlayButton
      key={key}
      children={shortText(action).replace('...', '')}
      onSubmit={() => {
        if (
          action instanceof MajorActionPlayAsCash &&
          !(action.card instanceof CashCard || action.card instanceof ResidenceCard)
        ) {
          // TODO Real modal instead of this
          if (!confirm('Are you sure you want to play this card as cash?')) {
            return Promise.reject()
          }
        }
        return props.chooseMajorAction(action)
      }}
      submitSuccess={props.chooseMajorActionSubmitSuccess}
    />
  )

  // One action plays immediately; several means a special property still needs its neighborhood picked.
  const playOrPickNeighborhood = (
    actions: NEL.NEList<AnyMajorAction>,
    key: string
  ) => {
    if (actions.length === 1) {
      return playDirectly(actions[0], key)
    }

    if (selectedCard instanceof SpecialPropertyCard) {
      const isChangeOrPlayToNeighborhood = (
        a: AnyMajorAction
      ): a is MajorActionPlayToNeighborhood | MajorActionChangeNeighborhood =>
        isInstance(MajorActionPlayToNeighborhood)(a) ||
        isInstance(MajorActionChangeNeighborhood)(a)

      return (
        <RegularOverlayButton
          key={key}
          children={shortText(actions[0])}
          onClick={() =>
            props.setClickableCardState(prevClickableCardState => ({
              ...prevClickableCardState,
              consideringMajorAction: {
                type: 'needsNeighborhood',
                card: selectedCard,
                // TODO it'd be nice to not have to define these since maybeMutateClickableCardState should do it
                clickablePortfolioNeighborhoodIds: S.create(
                  compact(
                    actions
                      .filter(isChangeOrPlayToNeighborhood)
                      .map(ptn => ptn.portfolioNeighborhoodId)
                  )
                ),
                selectedPortfolioNeighborhoodId: undefined,
              },
            }))
          }
        />
      )
    }

    console.log('dont know how to display actions for selected card:', {
      selectedCard,
      actions,
    })
    return null
  }

  return (
    <GenericOverlay>
      <DSTextDiv size="body-17-demi" color="white">
        {props.selectedCard.shortDescription(props.skin)}
      </DSTextDiv>
      {partialRecordKeys(grouped).map(key => {
        switch (key) {
          case 'slyDeal': {
            const actions = grouped[key]
            if (!actions) return null

            return (
              <RegularOverlayButton
                key={key}
                children={shortText(actions[0])}
                onClick={() =>
                  props.setClickableCardState(prevClickableCardState => ({
                    ...prevClickableCardState,
                    consideringMajorAction: {
                      type: 'slyDeal',
                      card: actions[0].card,
                      requestableCardIds: new Set(
                        actions.map(ma => ma.requestedCard.id)
                      ),
                      selectedCardToRequestId: undefined,
                    },
                  }))
                }
              />
            )
          }

          case 'forcedDeal': {
            const actions = grouped[key]
            if (!actions) return null

            return (
              <RegularOverlayButton
                key={key}
                children={shortText(actions[0])}
                onClick={() =>
                  props.setClickableCardState(prevClickableCardState => ({
                    ...prevClickableCardState,
                    consideringMajorAction: {
                      type: 'forcedDeal',
                      card: actions[0].card,
                      requestableCardIds: new Set(
                        actions.map(fd => fd.requestedCard.id)
                      ),
                      offerableCardIds: new Set(
                        actions.map(fd => fd.offeredCard.id)
                      ),
                      selectedCardToRequestId: undefined,
                      selectedCardToOfferId: undefined,
                    },
                  }))
                }
              />
            )
          }

          case 'debtCollector': {
            const actions = grouped[key]
            if (!actions) return null
            const clickablePlayerIds = new Set(
              actions.map(dc => dc.minorActorPlayerId)
            )

            return (
              <RegularOverlayButton
                key={key}
                children={shortText(actions[0])}
                onClick={() =>
                  props.setClickableCardState(prevClickableCardState => ({
                    ...prevClickableCardState,
                    consideringMajorAction: {
                      type: 'debtCollector',
                      card: actions[0].card,
                      clickablePlayerIds,
                      selectedPlayerId: S.soleMemberIfSingleton(clickablePlayerIds),
                    },
                  }))
                }
              />
            )
          }

          case 'directionalRent': {
            const actions = grouped[key]
            if (!actions) return null

            const clickablePlayerIds = new Set(
              actions.map(dr => dr.minorActorPlayerId)
            )
            const clickablePortfolioNeighborhoodIds = new Set(
              actions.map(dr => dr.portfolioNeighborhood.id)
            )

            return (
              <RegularOverlayButton
                key={key}
                children={shortText(actions[0])}
                onClick={() =>
                  props.setClickableCardState(prevClickableCardState => ({
                    ...prevClickableCardState,
                    consideringMajorAction: {
                      type: 'directionalRent',
                      card: actions[0].card,
                      clickablePlayerIds,
                      selectedPlayerId: S.soleMemberIfSingleton(clickablePlayerIds),
                      clickablePortfolioNeighborhoodIds,
                      selectedPortfolioNeighborhoodId: S.soleMemberIfSingleton(
                        clickablePortfolioNeighborhoodIds
                      ),
                    },
                  }))
                }
              />
            )
          }

          case 'rentDarkBlueGreen':
          case 'rentLightBlueBrown':
          case 'rentPinkOrange':
          case 'rentRedYellow':
          case 'rentUtilityRailroad': {
            // Universal rent's `type` is a 5-color union, so groupByDiscriminant can't narrow this
            // group. So, in practice this filter won't filter anything, but TS appreciates it.
            const universalRents = NEL.createSafe(
              availableMoves.filter(isInstance(MajorActionUniversalRent))
            )
            if (!universalRents) return null

            const [first] = universalRents
            if (universalRents.length === 1) {
              return playDirectly(first, key)
            }

            const clickablePortfolioNeighborhoodIds = new Set(
              universalRents.map(ur => ur.portfolioNeighborhood.id)
            )

            return (
              <RegularOverlayButton
                key={key}
                children={shortText(first)}
                onClick={() =>
                  props.setClickableCardState(prevClickableCardState => ({
                    ...prevClickableCardState,
                    consideringMajorAction: {
                      type: 'needsNeighborhood',
                      card: first.card,
                      clickablePortfolioNeighborhoodIds,
                      selectedPortfolioNeighborhoodId: S.soleMemberIfSingleton(
                        clickablePortfolioNeighborhoodIds
                      ),
                    },
                  }))
                }
              />
            )
          }

          case 'dealBreaker': {
            const actions = grouped[key]
            if (!actions) return null

            const clickablePortfolioNeighborhoodIds = new Set(
              actions.map(db => db.portfolioNeighborhoodId)
            )

            return (
              <RegularOverlayButton
                key={key}
                children={shortText(actions[0])}
                onClick={() =>
                  props.setClickableCardState(prevClickableCardState => ({
                    ...prevClickableCardState,
                    consideringMajorAction: {
                      type: 'needsNeighborhood',
                      card: actions[0].card,
                      clickablePortfolioNeighborhoodIds,
                      selectedPortfolioNeighborhoodId: S.soleMemberIfSingleton(
                        clickablePortfolioNeighborhoodIds
                      ),
                    },
                  }))
                }
              />
            )
          }

          case 'changeNeighborhood': {
            const actions = grouped[key]
            if (!actions) return null

            const {card} = actions[0]
            if (card instanceof DualPropertyCard) {
              // A dual is only ever two colors, so it gets a flip button per color and never the
              // neighborhood picker. The engine's various rules guarantee there's only ever one
              // valid (and distinct*) destination per dual color, so we just offer one each.
              // *: two lone PWCs can both be a valid destination, but there's no practical difference
              //    between choosing one or the other. Other that that edge case, you can only ever have
              //    one not-complete set per color at any time.
              const portfolio =
                clickableCardState.playerGamePosition.currentPlayer.portfolio
              const firstColor = card.standardPropertyTypesNEList()[0]

              const flipColor = (
                move: MajorActionChangeNeighborhood
              ): TStandardPropertyCard | undefined => {
                const {destination} = move
                if (destination.kind === 'newNeighborhood') return destination.color
                const nb = portfolio.neighborhoods.find(
                  pn => pn.id === destination.portfolioNeighborhoodId
                )
                if (!nb) return undefined
                return nb.neighborhood === 'alone' ? firstColor : nb.neighborhood
              }

              // One move per resulting color, preferring any existing set over a new
              // set, since the engine's heal would combine them anyway.
              const byColor = new Map<
                TStandardPropertyCard,
                MajorActionChangeNeighborhood
              >()
              for (const move of actions) {
                const color = flipColor(move)
                if (!color) continue
                if (
                  !byColor.has(color) ||
                  move.destination.kind === 'existingNeighborhood' // "prefer any existing"
                ) {
                  byColor.set(color, move)
                }
              }
              const colorFlips = [...byColor].map(([color, move]) => ({move, color}))

              return (
                <React.Fragment key={key}>
                  {colorFlips.map(({move, color}) => (
                    <PromiseOverlayButton
                      key={color}
                      children={
                        <>
                          {upperFirst(skins[props.skin].vocab.verbs.rotateDual)} to{' '}
                          {skins[props.skin].shortNeighborhoodNames[color]}
                          <NeighborhoodColorSwatch color={color} skin={props.skin} />
                        </>
                      }
                      onSubmit={() => props.chooseMajorAction(move)}
                      submitSuccess={props.chooseMajorActionSubmitSuccess}
                    />
                  ))}
                </React.Fragment>
              )
            }

            // The only other card that changes neighborhoods is a property wildcard, which picks a
            // destination from the neighborhood picker rather than flip buttons.
            return playOrPickNeighborhood(actions, key)
          }

          case 'birthday':
          case 'discard':
          case 'doubleTheRent':
          case 'passGo':
          case 'playAsCash':
          case 'playToNeighborhood':
          case 'residenceToCash': {
            const actions = grouped[key]
            if (!actions) return null

            return playOrPickNeighborhood(actions, key)
          }

          default:
            return unreachableCase(key)
        }
      })}
    </GenericOverlay>
  )
}

const CenterOfTableCards = (props: {
  skin: TSkin
  lastPlayedCard: AnyActionCard | AnyRentCard | undefined
  cardsInDeck: number
  classNames?: {
    wrapper?: string
    deck?: string
    lastPlayedCard?: string
  }
}) => {
  const {lastPlayedCard, cardsInDeck, skin, classNames} = props

  const cardWidth = CARD_WIDTH_PX.center
  const cardHeight = cardHeightFromWidth(cardWidth)

  const centerCardXPaddingPx = 0
  const centerCardYPaddingPx = 0
  const centerCardSpacerPx = 8

  const cardInCenterWidth = cardWidth + centerCardXPaddingPx * 2

  return (
    <div
      className={classnames(
        styles.cardsInCenter,
        classNames?.wrapper,
        css`
          width: ${cardWidth * 2 + centerCardSpacerPx + centerCardXPaddingPx * 4}px;
          height: ${cardHeight + centerCardYPaddingPx * 2}px;
        `
      )}
    >
      <div
        className={classnames(
          styles.cardInCenterWrap,
          css`
            width: ${cardInCenterWidth}px;
          `,
          classNames?.deck
        )}
      >
        <div style={{position: 'relative', width: cardWidth, height: cardHeight}}>
          {cardsInDeck > 0 && (
            <CardBackImage
              className={css`
                width: ${cardWidth}px;
              `}
              skin={skin}
              forceRoundedCornersOnSharpCornerSkins
            />
          )}
          <DSTextDiv size="tiny" color="white" className={styles.cardCounter}>
            {cardsInDeck}
          </DSTextDiv>
        </div>
      </div>
      <div
        className={classnames(
          styles.cardInCenterWrap,
          css`
            margin-left: ${centerCardSpacerPx}px;
            width: ${cardInCenterWidth}px;
          `,
          classNames?.lastPlayedCard
        )}
      >
        {lastPlayedCard && (
          <V2LobbyCardImage
            className={css`
              width: ${cardWidth}px;
            `}
            model={lastPlayedCard}
            invert={false}
            skin={skin}
            forceRoundedCornersOnSharpCornerSkins
          />
        )}
      </div>
    </div>
  )
}

const RegularOverlayButton = (
  ps: OmitStrict<IDSButtonProps, 'className' | 'size'> &
    Required<Pick<IDSButtonProps, 'onClick'>>
) => (
  <DSButton
    buttonStyle="tableAction"
    size="17px"
    className={styles.overlayButton}
    {...ps}
  />
)

const PromiseOverlayButton = (
  ps: OmitStrict<
    ISingleButtonFormProps,
    'globalErrorDisplay' | 'buttonStyle' | 'classNames'
  >
) => (
  <SingleButtonForm<TSubmitMajorActionResponse>
    globalErrorDisplay="toast"
    buttonStyle="tableAction"
    size="17px"
    classNames={{wrapper: styles.overlayButton}}
    {...ps}
  />
)
